import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link2, Link2Off, Plug, RefreshCw, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { usePaymentSettings } from '@/hooks/settings/usePaymentSettings';
import { useAcquisitionChannels } from '@/hooks/channels/useAcquisitionChannels';
import { useBillingSettings } from '@/hooks/settings/billing/useBillingSettings';
import { usePayabliConnection } from '@/hooks/settings/usePayabliConnection';
import { useChatbotConfig } from '@/hooks/settings/useChatbotConfig';
import { useConnectQuickBooks } from '@/hooks/settings/useConnectQuickBooks';
import { useDisconnectQuickBooks } from '@/hooks/settings/useDisconnectQuickBooks';
import { useQuickBooksStatus } from '@/hooks/settings/useQuickBooksStatus';
import { useDismissStripeConnect, useStripeConnect } from '@/hooks/settings/useStripeConnect';
import { useTriggerQbSync } from '@/hooks/settings/useTriggerQbSync';
import { useWisetackSettings } from '@/hooks/settings/useWisetackSettings';
import {
  useRevokeZapierConnection,
  useSetConnectionChannel,
  useZapierConfig,
  useZapierConnections,
  type ZapierConnection,
} from '@/hooks/settings/useZapierConnections';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import { useReviewSettings } from '@/hooks/review-platforms/useReviewSettings';
import { useCompanyCamStatus } from '@/hooks/integrations/useCompanyCamStatus';
import { useConnectCompanyCam } from '@/hooks/integrations/useConnectCompanyCam';
import { useDisconnectCompanyCam } from '@/hooks/integrations/useDisconnectCompanyCam';
import { useWebhookSubscriptions } from '@/hooks/webhooks/useWebhookSubscriptions';
import { usePartnerApiKeys } from '@/hooks/partnerApiKeys/usePartnerApiKeys';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { companyCamKeys } from '@/lib/queryKeys';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { cn } from '@/lib/utils';
import { QbDisconnectConfirmDialog } from '@/components/settings/accounting/QbDisconnectConfirmDialog';
import { StripeRequirementsCard } from '@/components/settings/StripeRequirementsCard';
import { WISETACK_CARD_SUBTITLE } from '@/lib/wisetack/brand';
import {
  FooterSpacer,
  IntegrationCard,
  IntegrationsAttentionBanner,
  PermissionHint,
  StripeDismissConfirmDialog,
  WisetackManageDialog,
  chatbotStatus,
  payabliStatus,
  quickBooksStatus,
  reviewPlatformsStatus,
  companyCamStatus,
  stripeStatus,
  partnerApiStatus,
  wisetackStatus,
  zapierStatus,
  type AttentionItem,
  type IntegrationStatusValue,
} from '@/components/organisms/settings-integrations';
import type {
  ChatbotConfig,
  PayabliConnection,
  PaymentSettings,
  QuickBooksStatus,
  WisetackSettings,
} from '@/types/settings';
import type { PartnerApiKey } from '@/types/partnerApiKey';
import type { WebhookSubscriptionView } from '@/types/webhook-subscription';
import type { AcquisitionChannel } from '@/types/acquisitionChannel';

/**
 * Connected Apps — the directory of every external service wired into Notifi.
 *
 * One uniform card per app, in one grid. No category sections: with eight
 * apps they only ever produced rows containing a single orphan card, so the
 * category rides along as a small label under each app's name instead. Cards
 * stretch to a common height per row, so the footers line up and the grid
 * reads as a set rather than a pile.
 *
 * Card bodies are deliberately capped at a blurb plus two or three facts.
 * Anything richer gets its own surface — Wisetack's enrollment panel opens in
 * a dialog, Stripe's blocking requirements get a full-width panel above the
 * grid — which is what keeps every card the same shape.
 *
 * Statuses come from `integration-status.ts`, which collapses eight unrelated
 * API shapes into one health vocabulary; that's what lets the header count and
 * the attention banner be written once.
 *
 * Permission model mirrors the backend exactly. Reading is `integrations:view`
 * or `company:view`; every write path (QuickBooks disconnect/sync, Stripe
 * connect/dismiss, Zapier revoke) is `company:edit`. Write actions stay
 * visible to viewers but disabled with an explanation, so the capability is
 * discoverable without handing anyone a button that can only 403.
 *
 * The Zapier OAuth connection is initiated from inside Zapier (Zapier drives
 * the OAuth authorize/token exchange against the operations OAuth provider),
 * so its "Connect" button can't run the OAuth flow itself — it just deep-links
 * the tenant into Zapier, where they add the "Create Lead in Notifi" action
 * and connect their account (which is where the Notifi login/consent page
 * appears). The card is only shown when the integration is configured for this
 * environment — the deep-link comes from the backend via
 * GET /oauth/zapier/config (`connectUrl`), sourced from per-environment
 * runtime config so the same frontend bundle works in every environment with
 * no rebuild. Until that is set the backend OAuth app / routing may not be
 * reachable, so we hide the card rather than send tenants into a setup flow
 * that can't complete. Tenants who are already connected still see it (via
 * GET /oauth/connections) so they can disconnect.
 */
/**
 * Query string the operations OAuth callback redirects back with. Without
 * these the return trip is silent: the callback bounces the browser here with
 * `status=error&message=...` and the page renders exactly as it did before,
 * which reads as "clicking Connect did nothing" rather than as a failure.
 */
interface IntegrationsSearch {
  provider?: string;
  status?: 'success' | 'error';
  message?: string;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/integrations')({
  validateSearch: (raw: Record<string, unknown>): IntegrationsSearch => ({
    provider: raw.provider ? String(raw.provider) : undefined,
    status: raw.status === 'success' || raw.status === 'error' ? raw.status : undefined,
    message: raw.message ? String(raw.message) : undefined,
  }),
  component: IntegrationsSettingsPage,
});

/** One entry in the grid — its status (for the header count and banner) and its card. */
interface IntegrationEntry {
  id: string;
  name: string;
  status: IntegrationStatusValue;
  node: ReactNode;
}

export function IntegrationsSettingsPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const canView = useHasAnyPermission('integrations:view', 'company:view');
  const canEdit = useHasAnyPermission('company:edit');
  const timeZone = useCompanyTimezone();

  const quickBooks = useQuickBooksStatus();
  const paymentSettings = usePaymentSettings();
  const billingSettings = useBillingSettings();
  const availableProcessors = billingSettings.data?.paymentMethods.availableProcessors;
  const stripeEnabled = availableProcessors?.stripe ?? true;
  const payabliEnabled = availableProcessors?.payabli ?? true;
  const payabliConnection = usePayabliConnection({ enabled: payabliEnabled });
  const wisetackSettings = useWisetackSettings();
  const reviewSettings = useReviewSettings();
  const companyCam = useCompanyCamStatus();
  const webhooks = useWebhookSubscriptions('partner');
  const partnerApiKeys = usePartnerApiKeys();
  const chatbot = useChatbotConfig();
  const zapierConnections = useZapierConnections();
  const zapierConfig = useZapierConfig();
  const zapierConnectionsList = zapierConnections.data ?? [];
  const zapierConnected = zapierConnectionsList.length > 0;
  const zapierConnectUrl = zapierConfig.data?.connectUrl ?? '';
  const zapierEnabled = zapierConnectUrl.length > 0;
  // Only fetched when the Zapier card can actually render (it's the only
  // consumer): GET /acquisition-channels requires leads:view, which viewers
  // scoped to integrations:view/company:view don't necessarily have, and an
  // unconditional call here would 403 for them on every load and Refresh.
  const acquisitionChannels = useAcquisitionChannels({ enabled: zapierEnabled || zapierConnected });

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Report the OAuth return trip. The callback is a bare redirect and renders
  // nothing itself, so this is the only place its outcome reaches the tenant —
  // without it a failed connect looks identical to a successful one.
  const { provider: callbackProvider, status: callbackStatus, message: callbackMessage } = search;
  useEffect(() => {
    if (!callbackProvider || !callbackStatus) return;

    const label = callbackProvider === 'companycam' ? 'CompanyCam' : callbackProvider;
    if (callbackStatus === 'success') {
      toast.success(`${label} connected`);
    } else {
      // The vendor's own wording is what makes these diagnosable — an
      // invalid_client reads very differently from a denied consent — so it is
      // passed through rather than flattened to "something went wrong".
      toast.error(`${label} could not be connected`, {
        description: callbackMessage?.slice(0, 300),
      });
    }
    if (callbackProvider === 'companycam') {
      void queryClient.invalidateQueries({ queryKey: companyCamKeys.all() });
    }

    // Drop the params so a reload or a back-navigation does not replay a
    // result the tenant has already been shown.
    void navigate({
      search: (prev) => ({
        ...prev,
        provider: undefined,
        status: undefined,
        message: undefined,
      }),
      replace: true,
    });
  }, [callbackProvider, callbackStatus, callbackMessage, navigate, queryClient]);

  const connectedReviewPlatforms =
    reviewSettings.data?.platforms.filter((platform) => platform.status === 'CONNECTED').length ??
    0;
  // Unfiltered — a bound connection may point at an archived or protected
  // channel, and the picker needs the full list to still show that channel's
  // name (disabled, not selectable). Which channels are OFFERABLE is decided
  // inside ZapierConnectionRow, not here.
  const acquisitionChannelsList = acquisitionChannels.data ?? [];

  // Stripe's requirements payload gets its own panel above the grid — it
  // carries far more detail than a one-line banner row, so the row would just
  // be noise sitting directly above it.
  const stripeRequirements = stripeEnabled ? paymentSettings.data?.stripe.requirements : null;
  const stripeRequirementCount =
    (stripeRequirements?.pastDue.length ?? 0) + (stripeRequirements?.currentlyDue.length ?? 0);
  const showStripeRequirementsPanel = stripeRequirementCount > 0;

  async function refreshAll() {
    setIsRefreshing(true);
    try {
      await Promise.allSettled([
        quickBooks.refetch(),
        paymentSettings.refetch(),
        billingSettings.refetch(),
        payabliEnabled ? payabliConnection.refetch() : Promise.resolve(),
        wisetackSettings.refetch(),
        reviewSettings.refetch(),
        companyCam.refetch(),
        webhooks.refetch(),
        partnerApiKeys.refetch(),
        chatbot.refetch(),
        zapierConnections.refetch(),
        zapierConfig.refetch(),
        zapierEnabled || zapierConnected ? acquisitionChannels.refetch() : Promise.resolve(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }

  // Ordered by how central each app is to running the business: books, then
  // getting paid, then reputation, then plumbing.
  const entries: IntegrationEntry[] = [];

  const qbStatus = quickBooksStatus(quickBooks.data, quickBooks.isError);
  entries.push({
    id: 'quickbooks',
    name: 'QuickBooks',
    status: qbStatus,
    node: (
      <QuickBooksCard
        status={qbStatus}
        isLoading={quickBooks.isLoading}
        isError={quickBooks.isError && !quickBooks.data}
        onRetry={() => void quickBooks.refetch()}
        data={quickBooks.data}
        timeZone={timeZone}
        canEdit={canEdit}
      />
    ),
  });

  if (stripeEnabled) {
    const status = stripeStatus(paymentSettings.data, paymentSettings.isError);
    entries.push({
      id: 'stripe',
      name: 'Stripe',
      status,
      node: (
        <StripeCard
          status={status}
          isLoading={paymentSettings.isLoading}
          isError={paymentSettings.isError && !paymentSettings.data}
          onRetry={() => void paymentSettings.refetch()}
          data={paymentSettings.data}
          canEdit={canEdit}
        />
      ),
    });
  }

  if (payabliEnabled) {
    const status = payabliStatus(payabliConnection.data, payabliConnection.isError);
    entries.push({
      id: 'payabli',
      name: 'Payabli',
      status,
      node: (
        <PayabliCard
          status={status}
          isLoading={payabliConnection.isLoading}
          isError={payabliConnection.isError}
          onRetry={() => void payabliConnection.refetch()}
          data={payabliConnection.data}
        />
      ),
    });
  }

  const wisetack = wisetackStatus(wisetackSettings.data, wisetackSettings.isError);
  entries.push({
    id: 'wisetack',
    name: 'Wisetack',
    status: wisetack,
    node: (
      <WisetackCard
        status={wisetack}
        isLoading={wisetackSettings.isLoading}
        isError={wisetackSettings.isError && !wisetackSettings.data}
        onRetry={() => void wisetackSettings.refetch()}
        settings={wisetackSettings.data}
      />
    ),
  });

  const reviews = reviewPlatformsStatus(
    connectedReviewPlatforms,
    reviewSettings.isError,
    Boolean(reviewSettings.data),
  );
  entries.push({
    id: 'review-platforms',
    name: 'Review platforms',
    status: reviews,
    node: (
      <ReviewPlatformsCard
        status={reviews}
        isLoading={reviewSettings.isLoading}
        isError={reviewSettings.isError && !reviewSettings.data}
        onRetry={() => void reviewSettings.refetch()}
        connectedCount={connectedReviewPlatforms}
        platformCount={reviewSettings.data?.platforms.length ?? 0}
        lastSync={latestDate(
          reviewSettings.data?.platforms.map((platform) => platform.lastSyncedAt),
          timeZone,
        )}
      />
    ),
  });

  const companyCamHealth = companyCamStatus(companyCam.data, companyCam.isError);
  entries.push({
    id: 'companycam',
    name: 'CompanyCam',
    status: companyCamHealth,
    node: (
      <CompanyCamCard
        status={companyCamHealth}
        isLoading={companyCam.isLoading}
        isError={companyCam.isError && !companyCam.data}
        onRetry={() => void companyCam.refetch()}
        available={companyCam.data?.available ?? false}
        connected={companyCam.data?.connected ?? false}
        companyId={companyCam.data?.externalAccountId ?? null}
        failedExports={companyCam.data?.failedExportCount ?? 0}
        canEdit={canEdit}
      />
    ),
  });

  const chat = chatbotStatus(chatbot.data?.enabled ?? false, chatbot.isError);
  entries.push({
    id: 'chatbot',
    name: 'Iris',
    status: chat,
    node: (
      <ChatbotCard
        status={chat}
        isLoading={chatbot.isLoading}
        isError={chatbot.isError}
        onRetry={() => void chatbot.refetch()}
        data={chatbot.data}
      />
    ),
  });

  if (zapierEnabled || zapierConnected) {
    const status = zapierStatus(zapierConnected, zapierConnections.isError);
    entries.push({
      id: 'zapier',
      name: 'Zapier',
      status,
      node: (
        <ZapierCard
          status={status}
          isLoading={zapierConnections.isLoading}
          isError={zapierConnections.isError && !zapierConnections.data}
          onRetry={() => void zapierConnections.refetch()}
          connections={zapierConnectionsList}
          channels={acquisitionChannelsList}
          channelsLoading={acquisitionChannels.isLoading}
          channelsError={acquisitionChannels.isError && !acquisitionChannels.data}
          connectUrl={zapierConnectUrl}
          timeZone={timeZone}
          canEdit={canEdit}
        />
      ),
    });
  }

  const partnerApi = partnerApiStatus(
    partnerApiKeys.data,
    webhooks.data,
    partnerApiKeys.isError || webhooks.isError,
  );
  entries.push({
    id: 'partner-api',
    name: 'Partner API',
    status: partnerApi,
    node: (
      <PartnerApiCard
        status={partnerApi}
        isLoading={webhooks.isLoading || partnerApiKeys.isLoading}
        isError={
          (webhooks.isError && !webhooks.data) || (partnerApiKeys.isError && !partnerApiKeys.data)
        }
        onRetry={() => {
          void webhooks.refetch();
          void partnerApiKeys.refetch();
        }}
        apiKeys={partnerApiKeys.data}
        subscriptions={webhooks.data}
        timeZone={timeZone}
      />
    ),
  });

  const connectedCount = entries.filter((entry) => entry.status.health === 'connected').length;

  const attentionItems: AttentionItem[] = entries
    .filter((entry) => entry.status.attentionReason != null)
    .filter((entry) => !(entry.id === 'stripe' && showStripeRequirementsPanel))
    .map((entry) => ({
      anchorId: entry.id,
      name: entry.name,
      reason: entry.status.attentionReason ?? '',
    }));

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        // The gutter is the only thing between the grid and the screen edge,
        // so it stays tight and responsive (the convention the other fullbleed
        // settings pages use). `overflow-x-hidden` matters because the base
        // class sets `overflow-y-auto`, and CSS resolves the *other* axis to
        // `auto` whenever one axis isn't `visible` — that's what puts a
        // horizontal scrollbar under a page that only ever scrolls vertically.
        contentClassName="overflow-x-hidden px-3 py-3 md:px-4 md:py-4"
        header={
          <>
            <div className="flex min-w-0 items-center gap-3">
              <span className="hidden size-8 shrink-0 items-center justify-center rounded-md bg-muted sm:flex">
                <Plug className="size-4 text-muted-foreground" />
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold">Connected Apps</h1>
                <p className="text-xs text-muted-foreground">
                  {canView
                    ? `${connectedCount} of ${entries.length} connected`
                    : 'Manage external services connected to Notifi.'}
                </p>
              </div>
            </div>
            {canView ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isRefreshing}
                onClick={() => void refreshAll()}
              >
                <RefreshCw className={cn('size-3.5', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
            ) : null}
          </>
        }
      >
        {!canView ? (
          <AccessDenied />
        ) : (
          <div className="w-full space-y-4">
            <IntegrationsAttentionBanner items={attentionItems} />

            {showStripeRequirementsPanel && paymentSettings.data ? (
              <StripeRequirementsCard
                requirements={paymentSettings.data.stripe.requirements}
                onResolve={() =>
                  void navigate({
                    to: '/dashboard/stripe',
                    search: { tab: 'payments', payment: null },
                  })
                }
              />
            ) : null}

            {/* auto-fill, not fixed breakpoints: the grid keeps adding columns
                for as long as another ~20rem card fits, so an ultrawide
                monitor gets a full row of eight instead of three columns and a
                lake of empty space. `min(20rem,100%)` is what stops the track
                from overflowing a phone narrower than the 20rem floor. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(20rem,100%),1fr))] items-stretch gap-4">
              {entries.map((entry) => (
                <Fragment key={entry.id}>{entry.node}</Fragment>
              ))}
            </div>
          </div>
        )}
      </DetailPageLayout>
    </>
  );
}

// ─── QuickBooks ──────────────────────────────────────────────────────────────

function QuickBooksCard({
  status,
  isLoading,
  isError,
  onRetry,
  data,
  timeZone,
  canEdit,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data: QuickBooksStatus | undefined;
  timeZone: string | null | undefined;
  canEdit: boolean;
}) {
  const navigate = Route.useNavigate();
  const connectQuickBooks = useConnectQuickBooks();
  const disconnectQuickBooks = useDisconnectQuickBooks();
  const triggerSync = useTriggerQbSync();
  const connected = data?.connected ?? false;

  return (
    <IntegrationCard
      anchorId="quickbooks"
      name="QuickBooks"
      category="Accounting"
      logo="quickbooks"
      description="Sync customers, invoices, payments, deposits and credits with your ledger."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'QuickBooks status could not be loaded.', onRetry } : null}
      facts={
        connected
          ? [
              { label: 'Company', value: data?.companyName ?? '—' },
              { label: 'Sync', value: formatValue(data?.syncStatus) ?? '—' },
              {
                label: 'Last sync',
                value: formatDateTime(data?.lastSyncAt, timeZone) ?? 'Never',
              },
            ]
          : undefined
      }
      actions={
        connected ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: '/dashboard/settings/accounting' })}
            >
              Settings
            </Button>
            <FooterSpacer />
            <PermissionHint show={!canEdit}>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEdit || triggerSync.isPending}
                onClick={() => triggerSync.mutate()}
              >
                <RefreshCw className={cn('size-3.5', triggerSync.isPending && 'animate-spin')} />
                {triggerSync.isPending ? 'Starting…' : 'Sync'}
              </Button>
            </PermissionHint>
            <PermissionHint show={!canEdit}>
              <QbDisconnectConfirmDialog
                isPending={disconnectQuickBooks.isPending}
                onConfirm={() => disconnectQuickBooks.mutate()}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!canEdit || disconnectQuickBooks.isPending}
                  >
                    <Link2Off className="size-3.5" />
                    Disconnect
                  </Button>
                }
              />
            </PermissionHint>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: '/dashboard/settings/accounting' })}
            >
              Settings
            </Button>
            <FooterSpacer />
            <Button
              size="sm"
              disabled={connectQuickBooks.isPending}
              onClick={() =>
                connectQuickBooks.mutate(undefined, {
                  onSuccess: ({ url }) => {
                    window.location.href = url;
                  },
                })
              }
            >
              <Link2 className="size-3.5" />
              {connectQuickBooks.isPending ? 'Opening…' : 'Connect'}
            </Button>
          </>
        )
      }
    />
  );
}

// ─── Stripe ──────────────────────────────────────────────────────────────────

function StripeCard({
  status,
  isLoading,
  isError,
  onRetry,
  data,
  canEdit,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data: PaymentSettings | undefined;
  canEdit: boolean;
}) {
  const navigate = Route.useNavigate();
  const connectStripe = useStripeConnect();
  const dismissStripe = useDismissStripeConnect();
  const stripe = data?.stripe;
  const connected = Boolean(stripe?.accountId);

  // Dismiss is only ever offered for a genuinely abandoned onboarding: Stripe
  // reports the account as pending with none of charges / payouts / details
  // completed. Anything further along has real state behind it.
  const canDismiss =
    canEdit &&
    stripe?.status === 'pending' &&
    stripe?.chargesEnabled === false &&
    stripe?.payoutsEnabled === false &&
    stripe?.detailsSubmitted === false;

  return (
    <IntegrationCard
      anchorId="stripe"
      name="Stripe"
      category="Payments"
      logo="stripe"
      description="Take card and ACH payments from estimates, invoices and the customer pay page."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'Stripe status could not be loaded.', onRetry } : null}
      facts={
        connected
          ? [
              { label: 'Account', value: stripe?.displayName ?? stripe?.accountId ?? '—' },
              { label: 'Charges', value: <BooleanText enabled={stripe?.chargesEnabled} /> },
              { label: 'Payouts', value: <BooleanText enabled={stripe?.payoutsEnabled} /> },
            ]
          : undefined
      }
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard/settings/billing/payments' })}
          >
            Settings
          </Button>
          <FooterSpacer />
          {connected ? (
            <>
              {canDismiss ? (
                <StripeDismissConfirmDialog
                  isPending={dismissStripe.isPending}
                  onConfirm={() => dismissStripe.mutate()}
                  trigger={
                    <Button variant="outline" size="sm" disabled={dismissStripe.isPending}>
                      <Link2Off className="size-3.5" />
                      Dismiss
                    </Button>
                  }
                />
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  void navigate({
                    to: '/dashboard/stripe',
                    search: { tab: 'payments', payment: null },
                  })
                }
              >
                Manage
              </Button>
            </>
          ) : (
            <PermissionHint show={!canEdit}>
              <Button
                size="sm"
                disabled={!canEdit || connectStripe.isPending}
                onClick={() => connectStripe.mutate()}
              >
                <Link2 className="size-3.5" />
                {connectStripe.isPending ? 'Opening…' : 'Connect'}
              </Button>
            </PermissionHint>
          )}
        </>
      }
    />
  );
}

// ─── Payabli ─────────────────────────────────────────────────────────────────

function PayabliCard({
  status,
  isLoading,
  isError,
  onRetry,
  data,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data: PayabliConnection | undefined;
}) {
  const navigate = Route.useNavigate();
  const connected = data?.connected ?? false;

  return (
    <IntegrationCard
      anchorId="payabli"
      name="Payabli"
      category="Payments"
      logo="payabli"
      description="Payment processing configured for your account by Notifi support."
      health={status.health}
      statusLabel={status.label}
      statusAdornment={
        data && connected ? (
          <Badge
            variant={data.mode === 'LIVE' ? 'secondary' : 'outline'}
            className="text-muted-foreground"
          >
            {data.mode}
          </Badge>
        ) : null
      }
      isLoading={isLoading}
      error={
        isError ? { message: 'Payabli connection status could not be loaded.', onRetry } : null
      }
      facts={
        connected
          ? [
              { label: 'Paypoint', value: data?.entryPointMasked ?? '—' },
              { label: 'Mode', value: data?.mode ?? '—' },
              // Credentials and delivery are separate facts — a paypoint can
              // be perfectly configured and still send us nothing.
              {
                label: 'Webhooks',
                value:
                  data?.webhooksRegistered === true
                    ? 'Registered'
                    : data?.webhooksRegistered === false
                      ? 'Not registered'
                      : 'Unknown',
              },
            ]
          : undefined
      }
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard/settings/billing/payments' })}
          >
            Settings
          </Button>
          <FooterSpacer />
        </>
      }
    />
  );
}

// ─── Wisetack ────────────────────────────────────────────────────────────────

function WisetackCard({
  status,
  isLoading,
  isError,
  onRetry,
  settings,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  settings: WisetackSettings | undefined;
}) {
  // Sandbox / mock are worth calling out — production is the expected case and
  // a badge for it would just be chrome on every tenant's screen.
  const environment = settings?.environment;
  const showEnvironment = environment != null && environment !== 'production';
  const approved = settings?.enrollmentState === 'approved';
  // Wisetack's wording: a merchant mid-application is resuming one, not
  // starting one (feedback deck, slide 4).
  const inProgress = settings?.enrollmentState === 'in_progress';

  return (
    <IntegrationCard
      anchorId="wisetack"
      name="Wisetack"
      category="Financing"
      logo="wisetack"
      description={WISETACK_CARD_SUBTITLE}
      health={status.health}
      statusLabel={status.label}
      statusAdornment={
        showEnvironment ? (
          <Badge variant="outline" className="text-muted-foreground">
            {formatEnumLabel(environment)}
          </Badge>
        ) : null
      }
      isLoading={isLoading}
      error={isError ? { message: 'Wisetack settings could not be loaded.', onRetry } : null}
      facts={settings ? [{ label: 'Enrollment', value: status.label }] : undefined}
      actions={
        <>
          <FooterSpacer />
          <WisetackManageDialog
            settings={settings}
            isLoading={isLoading}
            trigger={
              // Disabled while the query is in flight: the card footer renders
              // during loading, so without this a tenant can open a dialog
              // that has no settings to show yet.
              <Button variant={approved ? 'outline' : 'default'} size="sm" disabled={isLoading}>
                {approved
                  ? 'Financing tools'
                  : inProgress
                    ? 'Continue application'
                    : 'Set up financing'}
              </Button>
            }
          />
        </>
      }
    />
  );
}

// ─── Review platforms ────────────────────────────────────────────────────────

/**
 * CompanyCam.
 *
 * `unavailable` deliberately renders no Connect button: it means the
 * deployment has no CompanyCam credentials, which is an operator problem the
 * tenant cannot fix, and offering a button that 503s is worse than saying so.
 */
function CompanyCamCard({
  status,
  isLoading,
  isError,
  onRetry,
  available,
  connected,
  companyId,
  failedExports,
  canEdit,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  available: boolean;
  connected: boolean;
  companyId: string | null;
  failedExports: number;
  canEdit: boolean;
}) {
  const connectCompanyCam = useConnectCompanyCam();
  const disconnectCompanyCam = useDisconnectCompanyCam();

  return (
    <IntegrationCard
      anchorId="companycam"
      name="CompanyCam"
      category="Field operations"
      logo="companycam"
      description="Create a CompanyCam project when a job is scheduled, and pull field photos onto the job."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'CompanyCam status could not be loaded.', onRetry } : null}
      facts={[
        { label: 'Company', value: companyId ?? 'Not linked' },
        // Only when there is something to say. A permanent "Not sent: 0" row
        // would train the eye to skip the line that matters.
        ...(failedExports > 0
          ? [
              {
                label: 'Not sent',
                value: (
                  <span className="text-destructive">
                    {failedExports === 1 ? '1 photo' : `${failedExports} photos`}
                  </span>
                ),
              },
            ]
          : []),
      ]}
      actions={
        available ? (
          <>
            <FooterSpacer />
            <PermissionHint show={!canEdit}>
              {connected ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canEdit || disconnectCompanyCam.isPending}
                  onClick={() => disconnectCompanyCam.mutate()}
                >
                  <Link2Off className="size-3.5" />
                  {disconnectCompanyCam.isPending ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={!canEdit || connectCompanyCam.isPending}
                  onClick={() =>
                    connectCompanyCam.mutate(undefined, {
                      onSuccess: ({ url }) => {
                        window.location.href = url;
                      },
                    })
                  }
                >
                  <Link2 className="size-3.5" />
                  {connectCompanyCam.isPending ? 'Opening…' : 'Connect'}
                </Button>
              )}
            </PermissionHint>
          </>
        ) : (
          <>
            <FooterSpacer />
            <span className="text-xs text-muted-foreground">Not configured on this deployment</span>
          </>
        )
      }
    />
  );
}

function ReviewPlatformsCard({
  status,
  isLoading,
  isError,
  onRetry,
  connectedCount,
  platformCount,
  lastSync,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  connectedCount: number;
  platformCount: number;
  lastSync?: string;
}) {
  const navigate = Route.useNavigate();

  return (
    <IntegrationCard
      anchorId="review-platforms"
      name="Review platforms"
      category="Reputation"
      logo="reviews"
      description="Pull in reviews from Google, Facebook and the directories you rank on."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'Review settings could not be loaded.', onRetry } : null}
      facts={[
        { label: 'Sources', value: `${connectedCount} of ${platformCount}` },
        { label: 'Last sync', value: lastSync ?? 'Never' },
      ]}
      actions={
        <>
          <FooterSpacer />
          <Button
            variant="outline"
            size="sm"
            // Platforms is a tab on Settings > Reviews since NOT-576 H. The
            // old address still resolves — it redirects — so this kept
            // working and nothing failed; it just left for a page whose only
            // job is to send you back. Go straight to the tab.
            onClick={() =>
              void navigate({ to: '/dashboard/settings/reviews', search: { tab: 'platforms' } })
            }
          >
            {connectedCount > 0 ? 'Manage sources' : 'Connect a source'}
          </Button>
        </>
      }
    />
  );
}

// ─── Iris (AI assistant) ─────────────────────────────────────────────────────

function ChatbotCard({
  status,
  isLoading,
  isError,
  onRetry,
  data,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data: ChatbotConfig | undefined;
}) {
  const navigate = Route.useNavigate();

  return (
    <IntegrationCard
      anchorId="chatbot"
      name="Iris"
      category="AI assistant"
      logo="iris"
      description="Automated chat replies and visitor details captured straight into Notifi."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'Assistant settings could not be loaded.', onRetry } : null}
      facts={[
        { label: 'Business hours only', value: <BooleanText enabled={data?.businessHoursOnly} /> },
        { label: 'Collects email', value: <BooleanText enabled={data?.collectEmail} /> },
        { label: 'Collects phone', value: <BooleanText enabled={data?.collectPhone} /> },
      ]}
      actions={
        <>
          <FooterSpacer />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard/settings/chatbot' })}
          >
            Open Iris
          </Button>
        </>
      }
    />
  );
}

// ─── Zapier ──────────────────────────────────────────────────────────────────

function ZapierCard({
  status,
  isLoading,
  isError,
  onRetry,
  connections,
  channels,
  channelsLoading,
  channelsError,
  connectUrl,
  timeZone,
  canEdit,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** One entry per (clientId, userId) grant — two people connecting Zapier
   * each get a row that binds independently. */
  connections: ZapierConnection[];
  /** Unfiltered — archived and protected channels are still in here so a
   * bound row can display its name; each row decides what is selectable. */
  channels: AcquisitionChannel[];
  /** The channels query is independent of `connections` and can resolve
   * after it, or fail on its own (e.g. a role with `integrations:view` but
   * not `leads:view`) — both need to render as something other than an
   * empty picker that reads as "no channels exist". */
  channelsLoading: boolean;
  channelsError: boolean;
  connectUrl: string;
  timeZone: string | null | undefined;
  canEdit: boolean;
}) {
  const navigate = Route.useNavigate();
  const connected = connections.length > 0;

  return (
    <IntegrationCard
      anchorId="zapier"
      name="Zapier"
      category="Automation"
      logo="zapier"
      description="Send leads from website forms, Facebook Lead Ads and 6,000+ apps into Notifi."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'Zapier connections could not be loaded.', onRetry } : null}
      body={
        connected ? (
          <div className="mt-3.5 space-y-2.5 border-t border-border/60 pt-3.5">
            {connections.map((connection) => (
              // Keyed on the stable (clientId, userId) pair, not `id` — `id`
              // is the refresh-token row and is replaced on every token
              // rotation, so keying on it would silently re-key and remount
              // the row (losing any in-flight picker state) on a routine
              // token refresh.
              <ZapierConnectionRow
                key={`${connection.clientId}:${connection.userId}`}
                connection={connection}
                channels={channels}
                channelsLoading={channelsLoading}
                channelsError={channelsError}
                timeZone={timeZone}
                canEdit={canEdit}
              />
            ))}
          </div>
        ) : undefined
      }
      actions={
        <>
          {/* A connection with no matching channel yet has nowhere to go from
              the picker alone — this is the signpost to where channels are
              created and managed. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard/settings/leads' })}
          >
            Manage channels
          </Button>
          <FooterSpacer />
          {!connected && connectUrl ? (
            <Button size="sm" asChild>
              <a href={connectUrl} target="_blank" rel="noreferrer">
                <Link2 className="size-3.5" />
                Connect in Zapier
              </a>
            </Button>
          ) : null}
        </>
      }
    />
  );
}

/** Sentinel item value standing in for "no channel" — Radix `<Select>` throws
 * on an empty-string item value, so the unbind choice needs a real one. */
const UNBOUND_CHANNEL_VALUE = '__unbound__';

function ZapierConnectionRow({
  connection,
  channels,
  channelsLoading,
  channelsError,
  timeZone,
  canEdit,
}: {
  connection: ZapierConnection;
  /** Unfiltered — see the note on {@link ZapierCard}'s `channels` prop. */
  channels: AcquisitionChannel[];
  channelsLoading: boolean;
  channelsError: boolean;
  timeZone: string | null | undefined;
  canEdit: boolean;
}) {
  const setConnectionChannel = useSetConnectionChannel();
  // Per-row, not lifted to ZapierCard: a single shared mutation there fed
  // every row's `isRevoking`, so disconnecting one connection showed
  // "Disconnecting…" and disabled the Disconnect button on every OTHER row
  // too.
  const revokeConnection = useRevokeZapierConnection();

  function handleChannelChange(next: string) {
    setConnectionChannel.mutate({
      id: connection.id,
      // The mutation's DTO has no @IsOptional() on purpose — an omitted or
      // `undefined` value 400s server-side rather than silently skipping a
      // cross-tenant ownership check. `null` is the deliberate "unbind"
      // value, so a cleared picker must send it explicitly.
      acquisitionChannelId: next === UNBOUND_CHANNEL_VALUE ? null : next,
    });
  }

  // Selectable = offerable on a FRESH binding: archived channels are done,
  // and the four protected slugs (unknown / repeat-customer / imported /
  // manual-entry) are provenance markers the backend writes by slug, not
  // real acquisition sources — binding a Zap to one would corrupt
  // attribution reporting.
  const selectableChannels = channels.filter((channel) => channel.isActive && !channel.isProtected);

  // The channel this row is ALREADY bound to may not be selectable (archived
  // after binding, or — pre-existing data — bound to a protected slug). Radix
  // renders the trigger blank when `value` matches no `SelectItem`, which
  // reads as "unbound" and invites a false-information rebind. Surface it
  // instead as one extra, disabled option carrying its real name.
  const boundChannel =
    channels.find((channel) => channel.id === connection.acquisitionChannelId) ?? null;
  const boundChannelIsSelectable =
    boundChannel != null && selectableChannels.some((channel) => channel.id === boundChannel.id);

  return (
    <div
      data-testid="zapier-connection-row"
      className="space-y-2 rounded-lg border border-border/60 px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium leading-5">{connection.clientName}</p>
          {/* `userLabel`, never `userId` — the id is a UUID and is the binding
              key, not something an admin can identify a colleague by. The
              fallback covers deploy skew: an operations build older than
              `userLabel` omits the field, and a bare read would render
              "Consented by " with nothing. `||` rather than `??` so an empty
              string is caught too — blank is the exact failure being avoided. */}
          <p className="truncate text-xs text-muted-foreground">
            Consented by {connection.userLabel || connection.userId}
          </p>
          {/* Reference info, not a call to action — what the OAuth grant permits
              and when it was made. This is the only surface an admin has to see
              a Zap's scopes, so it must survive even when the row is otherwise
              quiet. */}
          <p className="text-xs text-muted-foreground">
            Scopes: {connection.scopes.join(', ') || '—'}
          </p>
          <p className="text-xs text-muted-foreground">
            Connected {formatDateTime(connection.createdAt, timeZone) ?? '—'}
          </p>
        </div>
        {/* Revoke hits the company:edit-guarded /oauth/connections/:id/revoke
            endpoint, so viewers get the button disabled rather than a 403. */}
        <PermissionHint show={!canEdit}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canEdit || revokeConnection.isPending}
            onClick={() => revokeConnection.mutate(connection.id)}
          >
            <Link2Off className="size-3.5" />
            {revokeConnection.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </PermissionHint>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Rendered disabled (not hidden) without company:edit, matching the
            revoke button above — the capability stays discoverable. */}
        <PermissionHint show={!canEdit}>
          {channelsError ? (
            // A failed channels query (e.g. missing leads:view) must not
            // look identical to "this tenant has no channels" — that silently
            // blanks every picker with no way to tell why.
            <p role="alert" className="text-xs text-destructive">
              Channels could not be loaded.
            </p>
          ) : channelsLoading ? (
            // The channels query is independent of (and can resolve after)
            // the connections query — without this, a bound row would
            // briefly render the same blank trigger as the F1 bug on every
            // page load.
            <Skeleton className="h-8 w-full max-w-56" />
          ) : (
            <Select
              value={connection.acquisitionChannelId ?? UNBOUND_CHANNEL_VALUE}
              onValueChange={handleChannelChange}
              disabled={!canEdit || setConnectionChannel.isPending}
            >
              <SelectTrigger size="sm" className="w-full max-w-56">
                <SelectValue placeholder="Not attributed yet" />
              </SelectTrigger>
              <SelectContent>
                {/* Unbound is the normal, default state — a prompt to pick a
                    channel, not a warning or a failure. */}
                <SelectItem value={UNBOUND_CHANNEL_VALUE}>Not attributed yet</SelectItem>
                {selectableChannels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </SelectItem>
                ))}
                {boundChannel && !boundChannelIsSelectable ? (
                  <SelectItem value={boundChannel.id} disabled>
                    {boundChannel.isActive ? boundChannel.name : `${boundChannel.name} (archived)`}
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          )}
        </PermissionHint>
        <span className="shrink-0 text-xs text-muted-foreground">
          {connection.lastReceivedAt
            ? (formatDateTime(connection.lastReceivedAt, timeZone) ?? 'No leads yet')
            : 'No leads yet'}
        </span>
      </div>
    </div>
  );
}

// ─── Partner API ─────────────────────────────────────────────────────────────

function PartnerApiCard({
  status,
  isLoading,
  isError,
  onRetry,
  apiKeys,
  subscriptions,
  timeZone,
}: {
  status: IntegrationStatusValue;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  apiKeys: PartnerApiKey[] | undefined;
  subscriptions: WebhookSubscriptionView[] | undefined;
  timeZone: string | null | undefined;
}) {
  const navigate = Route.useNavigate();
  const activeKeys = apiKeys?.filter((key) => key.status === 'active').length ?? 0;
  const enabledWebhooks = subscriptions?.filter((subscription) => subscription.enabled).length ?? 0;
  const configured = activeKeys > 0 || enabledWebhooks > 0;

  return (
    <IntegrationCard
      anchorId="partner-api"
      name="Partner API"
      category="Developer"
      logo="partnerApi"
      description="Issue partner credentials and push signed change notifications to their systems."
      health={status.health}
      statusLabel={status.label}
      isLoading={isLoading}
      error={isError ? { message: 'Partner API settings could not be loaded.', onRetry } : null}
      facts={
        configured
          ? [
              { label: 'Active API keys', value: activeKeys },
              { label: 'Enabled webhooks', value: enabledWebhooks },
              {
                label: 'Last delivered',
                value:
                  latestDate(
                    subscriptions?.map((subscription) => subscription.lastDeliveredAt),
                    timeZone,
                  ) ?? 'Never',
              },
            ]
          : undefined
      }
      actions={
        <>
          <FooterSpacer />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard/settings/api-keys' })}
          >
            {configured ? 'Manage' : 'Get started'}
          </Button>
        </>
      }
    />
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function BooleanText({ enabled }: { enabled: boolean | undefined }) {
  return (
    <span className={cn(!enabled && 'font-normal text-muted-foreground')}>
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}

function AccessDenied() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-xl border border-dashed bg-card px-6 py-16 text-center">
      <span className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <ShieldCheck className="size-5" />
      </span>
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">You don&apos;t have access to Connected Apps</h2>
        <p className="text-sm text-muted-foreground">
          Viewing connected apps needs the Integrations view or Company view permission. Ask an
          administrator to grant it.
        </p>
      </div>
    </div>
  );
}

function formatValue(value: string | null | undefined) {
  if (!value) return undefined;
  return value.replaceAll('_', ' ');
}

function formatDateTime(value: string | null | undefined, timeZone?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatDateTimeInTimeZone(value, timeZone, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatEnumLabel(value: string) {
  return value.replaceAll('_', ' ').replace(/^./, (char) => char.toUpperCase());
}

function latestDate(
  values: Array<string | null | undefined> | undefined,
  timeZone?: string | null,
) {
  const latest = values
    ?.map((value) => (value ? new Date(value) : null))
    .filter((value): value is Date => value != null && !Number.isNaN(value.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0];

  return latest
    ? formatDateTimeInTimeZone(latest.toISOString(), timeZone, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : undefined;
}
